import { GridOptions, RowNode } from 'ag-grid-community';
import React, {
  ComponentType,
  FC,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import { useFilterEffect } from 'components/organisms/tasks/filters';
import { useOnGridReady } from './use-on-grid-ready';

type KnownProps = Pick<
  GridOptions,
  'doesExternalFilterPass' | 'isExternalFilterPresent'
>;

export type Filter = (record: Record<string, unknown>) => boolean;

export type ExternalFilterProps = {
  filter?: Filter;
  search?: Filter;
  isPaginated?: boolean;
};

export const withExternalFilter = <
  T extends KnownProps & Pick<GridOptions, 'onGridReady'>,
>(
  Component: ComponentType<T>,
): FC<Omit<T, keyof KnownProps> & ExternalFilterProps> => {
  const Next: FC<Omit<T, keyof KnownProps> & ExternalFilterProps> = ({
    onGridReady: onReady,
    filter: actual,
    search,
    isPaginated,
    ...props
  }) => {
    const [api, , onGridReady] = useOnGridReady(onReady);
    const filter = useRef<Filter | null>(actual ?? null);
    const searchFn = useRef(search);

    useEffect(() => {
      searchFn.current = search;
      api.current?.onFilterChanged();
    }, [api, search]);
    const isExternalFilterPresent = useCallback(() => {
      const filterFunction = filter.current || searchFn.current;

      return filterFunction != null;
    }, [filter, searchFn]);
    const doesExternalFilterPass = useCallback(
      (node: RowNode) => {
        const isFiltered = filter.current ? filter.current?.(node.data) : true;
        const isSearchable = searchFn.current
          ? searchFn.current(node.data)
          : true;

        return isFiltered && isSearchable;
      },
      [filter, searchFn],
    );

    useEffect(() => {
      filter.current = actual ?? null;
    }, [filter, actual]);

    useFilterEffect(() => {
      if (!isPaginated) {
        return;
      }
      api.current?.onFilterChanged();
    });

    useEffect(() => {
      if (isPaginated) {
        return;
      }
      api.current?.onFilterChanged();
    }, [api, actual, isPaginated]);

    return (
      <Component
        {...(props as any)}
        isPaginated={isPaginated}
        onGridReady={onGridReady}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={doesExternalFilterPass}
      />
    );
  };

  return setDisplayName(wrapDisplayName(Component, 'WithExternalFilter'))(Next);
};
